import {
  Component,
  JSX,
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { computePosition, flip, shift, offset, arrow } from "@floating-ui/dom";

export interface TooltipProps {
  /** Element to anchor the tooltip to */
  anchor: HTMLElement | null;
  /** Tooltip content - can be text or JSX */
  content: string | JSX.Element;
  /** Unique identifier for this tooltip (used for dismiss persistence) */
  id: string;
  /** Called when the user dismisses the tooltip */
  onDismiss: () => void;
  /** Whether the tooltip should be visible */
  show: boolean;
}

/**
 * Feature discovery tooltip component.
 * Renders a dismissable overlay bubble anchored to a specific element.
 * Uses @floating-ui/dom for smart positioning that handles viewport edges.
 */
export const Tooltip: Component<TooltipProps> = (props) => {
  let tooltipRef: HTMLDivElement | undefined;
  let arrowRef: HTMLDivElement | undefined;

  const [position, setPosition] = createSignal({ x: 0, y: 0 });
  const [arrowPosition, setArrowPosition] = createSignal({ x: 0, y: 0 });

  /**
   * Update tooltip position using floating-ui
   */
  const updatePosition = async () => {
    if (!props.anchor || !tooltipRef || !arrowRef) return;

    const { x, y, middlewareData } = await computePosition(
      props.anchor,
      tooltipRef,
      {
        placement: "bottom",
        middleware: [
          offset(12), // Distance from anchor
          flip(), // Flip to opposite side if doesn't fit
          shift({ padding: 8 }), // Shift along axis to stay in viewport
          arrow({ element: arrowRef }), // Position arrow
        ],
      },
    );

    setPosition({ x, y });

    if (middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      setArrowPosition({ x: arrowX ?? 0, y: arrowY ?? 0 });
    }
  };

  createEffect(() => {
    if (props.show && props.anchor) {
      updatePosition();
    }
  });

  onMount(() => {
    if (!props.show) return;

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    onCleanup(() => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    });
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      props.onDismiss();
    }
  };

  return (
    <Show when={props.show && props.anchor}>
      {/* Semi-transparent backdrop - non-blocking */}
      <div
        class="fixed inset-0 z-40 bg-scrim/10 pointer-events-none"
        aria-hidden="true"
      />

      {/* Tooltip bubble */}
      <div
        ref={tooltipRef}
        role="tooltip"
        class="fixed z-50 max-w-sm bg-ink text-paper rounded shadow-overlay pointer-events-auto"
        style={{
          left: `${position().x}px`,
          top: `${position().y}px`,
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Arrow */}
        <div
          ref={arrowRef}
          class="absolute w-3 h-3 bg-ink rotate-45"
          style={{
            left: `${arrowPosition().x}px`,
            top: "-6px",
          }}
        />

        {/* Content */}
        <div class="relative p-4">
          {/* Close button */}
          <button
            type="button"
            onClick={props.onDismiss}
            class="absolute top-2 right-2 text-paper/70 hover:text-paper transition-colors"
            aria-label="Dismiss tip"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Tooltip content */}
          <div class="pr-6 text-sm text-paper">{props.content}</div>

          {/* Dismiss button */}
          <div class="mt-3 flex justify-end">
            <button
              type="button"
              onClick={props.onDismiss}
              class="px-3 py-1.5 text-sm font-medium text-paper/70 hover:text-paper rounded-md transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
};
